/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { guidFor } from '@ember/object/internals';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';

import type { IBtnColor, TButtonSize } from '../bv-btn';
import type { BvLabelledSignature } from '../bv-labelled';
import type { PowerSelectArgs, Select } from 'ember-power-select/components/power-select';
import type { TSize } from 'volta/utils/style-utils';
import type { IntlService } from 'ember-intl';
import type { ContentValue } from '@glint/template';
import InfinityModel from 'ember-infinity/lib/infinity-model';

type ComponentArgs = BvLabelledSignature['Args'] & Omit<PowerSelectArgs, 'options' | 'horizontalPosition'>;

export interface BvSelectArgs extends ComponentArgs {
  id?: string;
  dropdownSize?: TSize;
  // Trigger btn color
  color?: IBtnColor;
  // Trigger btn size
  size?: TButtonSize;
  // Fill container
  fill?: boolean;
  // Trigger btn is subtle ?
  subtle?: boolean;
  // Trigger btn icon before
  iconLeft?: string;
  // Trigger prefix label (Like "Sort by" for example)
  prefix?: ContentValue;
  // Trigger suffix label
  suffix?: ContentValue;
  // If set; get the value from the option with `get`
  bufferSize?: number;
  dataName?: string;
  hideTriggerCaret?: boolean;
  infiniteScroll?: boolean;
  isLoading?: boolean;
  isRequired?: boolean;
  labelAction?: BvLabelledSignature['Args']['action'];
  labelClass?: string;
  labelInfo?: BvLabelledSignature['Args']['info'];
  labelInline?: BvLabelledSignature['Args']['inline'];
  optionHeight?: number;
  optionKey?: string;
  optionPath?: string;
  plain?: boolean;
  renderAll?: boolean;
  resourceNamePlural?: string;
  focused?: boolean;
  selectedPath?: string;
  tooltipText?: string;
  showBeforeOptions?: boolean;
  options?: any[] | PromiseProxy<any[]> | InfinityModel;
  outline?: boolean;
  verticalPosition?: 'auto' | 'above' | 'below';
  horizontalPosition?:
    | 'auto'
    | 'right'
    | 'left'
    | 'center'
    | 'auto-right'
    | 'auto-left'
    | 'auto-left';
  typeAheadMatcher?: (option: unknown, searchTerm?: string) => void;
}

export interface BvSelectSignature {
  Args: BvSelectArgs;
  Blocks: {
    default: [any, Select];
  };
  Element: HTMLElement;
}

export default class BvSelectComponent extends Component<BvSelectSignature> {
  @service intl!: IntlService;

  id = this.args.id ?? `BvSelect-${guidFor(this)}`;

  get inifinityModel() {
    return this.args.options instanceof InfinityModel ? this.args.options : undefined;
  }

  get options() {
    return this.args.options as any[] | PromiseProxy<any[]> | undefined;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvSelect: typeof BvSelectComponent;
    'bv-select': typeof BvSelectComponent;
  }
}
